import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeDollarSign, ClipboardCheck, MapPin } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1508873535684-277a3cbcc4e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxoYW5keW1hbiUyMHRvb2xzJTIwY2FycGVudHJ5JTIwcGFpbnRlciUyMGhvdXNlJTIwcmVwYWlyfGVufDB8fHx8MTc4ODcxMTAwNXww&ixlib=rb-4.1.0&q=85";

const reveal = (delay) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(#f59e0b 1px, transparent 1px), linear-gradient(90deg, #f59e0b 1px, transparent 1px)", backgroundSize: "72px 72px" }} />
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto w-full grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
                    className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400 mb-6" data-testid="hero-eyebrow">
            Fix &amp; Finish — Handyman Services
          </motion.p>
          <h1 className="font-condensed font-black uppercase tracking-tight leading-[0.9] text-6xl sm:text-7xl lg:text-8xl">
            <span className="mask-line"><motion.span {...reveal(0.2)} data-testid="hero-line-1">We Fix It.</motion.span></span>
            <span className="mask-line"><motion.span {...reveal(0.38)} className="text-amber-500" data-testid="hero-line-2">We Finish It.</motion.span></span>
            <span className="mask-line"><motion.span {...reveal(0.56)} className="text-outline" data-testid="hero-line-3">You Pay On Site.</motion.span></span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
                    className="text-base leading-relaxed text-slate-300 max-w-lg mt-8" data-testid="hero-sub">
            Lawns, gutters, driveways, fences, paint and every odd job in between.
            Book online in under a minute — pay nothing until the work is done,
            right there at your place.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1 }}
                      className="flex flex-wrap items-center gap-4 mt-10">
            <a href="#booking" data-testid="hero-book-cta"
               className="group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-condensed font-bold uppercase tracking-wide text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(245,158,11,0.35)]">
              Book Job Now
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="#gallery" data-testid="hero-gallery-cta"
               className="inline-flex items-center gap-3 border border-amber-500/40 hover:border-amber-500 text-slate-200 font-condensed font-bold uppercase tracking-wide text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-1">
              See The Proof
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.2 }}
                      className="flex flex-wrap gap-x-8 gap-y-3 mt-12">
            {[
              { icon: BadgeDollarSign, text: "100% Pay On-Site" },
              { icon: ClipboardCheck, text: "Free Upfront Estimates" },
              { icon: MapPin, text: "Local & Reliable" },
            ].map(({ icon: Icon, text }, i) => (
              <div key={text} data-testid={`hero-stat-${i}`} className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-400">{text}</span>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div className="md:col-span-5 relative hidden md:block"
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <div className="absolute -top-4 -left-4 w-24 h-3 hazard-stripes z-20" />
          <div className="absolute -bottom-4 -right-4 w-24 h-3 hazard-stripes z-20" />
          <motion.div style={{ y: imgY, rotate: imgRotate }}
                      className="relative border border-amber-500/30 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <img src={HERO_IMG} alt="Handyman tools and craftsmanship" data-testid="hero-image"
                 className="w-full h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 bg-slate-950/85 backdrop-blur px-4 py-2 border border-amber-500/30">
              <span className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400">Pay Directly At Job Site</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
